import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription, firstValueFrom } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ApDarkmodeService } from '@ap/darkmode';
import { ApModalComponent, ApModalService, ModalClosedReason, ModalOptions, ModalSize } from '@ap/modal';

import { FeedbackModalOptions, LastUsedSettings } from './feedback/feedback.model';
import { FeedbackModalComponent } from './feedback/modal/modal.component';
import { MoodOptions } from './mijn-feedback.constants';
import content from './mijn-feedback.content.json';
import { MijnFeedbackSettings, RequiredMetadata, RetrievableMetadata, Variants } from './mijn-feedback.model';
import { MijnFeedbackService } from './mijn-feedback.service';

/**
 * @module MijnFeedbackModule
 * @ngdoc component
 * @name MijnFeedbackComponent
 */
@Component({
    selector: 'mijn-feedback',
    templateUrl: 'mijn-feedback.component.html',
    styleUrls: ['./mijn-feedback.component.scss'],
    standalone: false
})
export class MijnFeedbackComponent implements OnInit, OnDestroy {
    @Input({ required: true })
    public mijnFeedbackSettings: MijnFeedbackSettings;

    @Input({ required: true })
    public requiredMetadata: RequiredMetadata;

    @Input()
    public platform = 'MIJN';

    @Input()
    public lastUsedSettings?: LastUsedSettings;

    @Output()
    public toContact: EventEmitter<void> = new EventEmitter();

    @Output()
    public closeMijnFeedback: EventEmitter<void> = new EventEmitter();

    public moodOptions = MoodOptions;
    public modeSetting = 'light';
    public feedbackVariants = Variants;

    private modalComponent: ApModalComponent;
    private readonly subscriptions: Subscription = new Subscription();

    public constructor(
        private readonly contentService: ApContentService,
        private readonly mijnFeedbackService: MijnFeedbackService,
        private readonly modalService: ApModalService,
        private readonly darkmodeService: ApDarkmodeService
    ) {}

    public get type(): Variants {
        return this.mijnFeedbackSettings.type ? this.mijnFeedbackSettings.type : Variants.MOOD_FULL;
    }

    public get sidebarLabel(): string {
        return this.mijnFeedbackSettings.sidebarLabel
            ? this.mijnFeedbackSettings.sidebarLabel
            : this.contentService.get('mijn-feedback.sidebar.label.default');
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);

        this.modeSetting = this.darkmodeService.getDarkmodeSetting();

        this.subscriptions.add(
            this.mijnFeedbackService.getIsFeedbackPosted().subscribe((posted) => {
                if (posted) {
                    this.closeMijnFeedback.emit();
                }
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public async openFeedback(mood: string): Promise<void> {
        const feedbackOptions: FeedbackModalOptions = {
            channelId: this.mijnFeedbackSettings.channelId,
            moodId: this.mijnFeedbackSettings.moodId,
            moodTitle: this.mijnFeedbackSettings.moodTitle,
            mood,
            commentsId: this.mijnFeedbackSettings.commentsId,
            lastUsedCategory: this.lastUsedSettings?.lastUsedCategory ?? null,
            lastUsedName: this.lastUsedSettings?.lastUsedName ?? null,
            customThankYouMessage: this.mijnFeedbackSettings.customThankYouMessage ?? null,
            metadata: await this.getMetadata(this.mijnFeedbackSettings.retailMetadata ?? null),
            retailMetadata: this.mijnFeedbackSettings.retailMetadata ?? null
        };

        this.modalComponent = this.modalService.open(FeedbackModalComponent, this.getModalOptions(feedbackOptions));
        this.subscriptions.add(
            this.modalComponent.afterClosed().subscribe((result) => {
                if (result.reason === ModalClosedReason.DECLINED) {
                    this.toContact.emit();
                }
            })
        );
    }

    public getSvgSource(svgName: string) {
        return `/icon/mood-icon-${svgName}-${this.modeSetting}.svg`;
    }

    private getModalOptions(feedbackOptions: FeedbackModalOptions): ModalOptions<FeedbackModalOptions> {
        const modalOptions = new ModalOptions(feedbackOptions);
        modalOptions.title = this.contentService.get('mijn-feedback.modal.title');
        modalOptions.size = ModalSize.LARGE;
        return modalOptions;
    }

    private async getMetadata(retailMetadata: string): Promise<{ [key: string]: string }> {
        const customerType = !!retailMetadata ? retailMetadata : await firstValueFrom(this.mijnFeedbackService.getCustomerType());
        return {
            ...(this.mijnFeedbackSettings.metadata || {}),
            ...({
                customerType,
                platform: this.platform,
                userAgent: window.navigator.userAgent,
                brandNumber: this.mijnFeedbackService.merkNummer
            } as RetrievableMetadata),
            ...this.requiredMetadata
        };
    }
}
